import { useMemo, useCallback, useEffect } from 'react';
import { filter, pipe, prop, includes, map, pick } from 'ramda';

import api from 'api';
import { useAsyncState } from 'utils/useAsyncState';
import { FILE_ID, FILE_URL, FILE_URL_EXPIRES, BUCKET, MIMETYPE, ORIGINAL_NAME, OFFLINE } from 'api/consts';

const loadImage = async (image) => {
  const blobUrl = await api.loadFile({
    ...pick([FILE_ID, FILE_URL_EXPIRES, BUCKET, MIMETYPE, OFFLINE], image),
    [FILE_URL]: image[FILE_URL] || image.path,
  });

  return { ...image, [FILE_ID]: image[FILE_ID] || image[FILE_URL] || image.path, blobUrl };
};

export const useLoadImage = (items) => {
  const [data, setData] = useAsyncState({ loading: true, list: [] });
  const images = useMemo(
    () =>
      pipe(
        filter(pipe(prop(MIMETYPE), includes('image'))),
        map((item) => ({
          ...pick(
            [FILE_ID, FILE_URL, FILE_URL_EXPIRES, BUCKET, MIMETYPE, ORIGINAL_NAME, OFFLINE, 'path', 'date', 'isCurrent'],
            item
          ),
          description: item.date
            ? `${item.description || item[ORIGINAL_NAME]} (${item.date})`
            : item.description || item[ORIGINAL_NAME],
        }))
      )(items),
    [items]
  );

  const requestImages = useCallback(
    async (values) => {
      try {
        const list = await Promise.all(map(loadImage)(values));

        setData({ loading: false, list });
      } catch (e) {
        setData(({ list }) => ({ list, error: 'Beim Laden der Daten tritt ein Fehler auf.' }));
      }
    },
    [setData]
  );

  useEffect(() => {
    requestImages(images);
  }, [images, requestImages]);

  useEffect(
    () => () => {
      if (data?.list?.length) {
        data.list.forEach(({ blobUrl }) => {
          (window.URL || window.webkitURL).revokeObjectURL(blobUrl);
        });
      }
    },
    [data.list]
  );

  return data;
};
